import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
} from "react-table";
import SkillService from "../Service/SkillService";

function SkillManagementPage() {
  const location = useLocation();
  const { user } = location.state || {};

  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(user.track);
  const [formData, setFormData] = useState({ skillId: null, skillName: "", track: user.track });
  const [isEditMode, setIsEditMode] = useState(false);
  const [search, setSearch] = useState("");
  const [newlyAddedSkillId, setNewlyAddedSkillId] = useState(null);
  const [manualPageIndex, setManualPageIndex] = useState(0);

  useEffect(() => {
    fetchAllTracks();
  }, []);

  useEffect(() => {
    setManualPageIndex(0); // reset to first page on track change
    fetchSkillsByTrack(selectedTrack);
  }, [selectedTrack]);

  const fetchAllTracks = async () => {
    try {
      const response = await SkillService.getAllTracks();
      setTracks(response);
    } catch (err) {
      console.error("Error fetching tracks:", err);
      alert("Could not load tracks.");
    }
  };

  const fetchSkillsByTrack = async (track) => {
    try {
      setLoading(true);
      const response = await SkillService.getAllSkillsByTrack(track);
      setSkills(response);
    } catch (error) {
      console.error("Error fetching skills:", error);
      alert("Failed to fetch skills.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, skillName: e.target.value });
  };

  const handleTrackChange = (e) => {
    const track = e.target.value;
    setSelectedTrack(track);
    if (!isEditMode) {
      setFormData((prev) => ({ ...prev, track }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.skillName.trim()) {
      alert("Skill name is required.");
      return;
    }

    try {
      if (isEditMode) {
        await SkillService.updateSkill(formData);
        alert("Skill updated successfully.");

        // Update skill locally
        setSkills((prev) =>
          prev.map((skill) =>
            skill.skillId === formData.skillId ? { ...skill, ...formData } : skill
          )
        );
      } else {
        const savedSkill = await SkillService.addSkill({
          skillName: formData.skillName,
          track: selectedTrack,
        });
        alert("Skill added successfully.");
        await fetchSkillsByTrack(selectedTrack);
        setNewlyAddedSkillId(savedSkill.skillId);
      }

      setFormData({ skillId: null, skillName: "", track: selectedTrack });
      setIsEditMode(false);
    } catch (error) {
      console.error("Error saving skill:", error);
      alert("Failed to save skill.");
    }
  };

  const handleEdit = (skill) => {
    setFormData(skill);
    setSelectedTrack(skill.track);
    setIsEditMode(true);
  };

  const handleDelete = async (skillId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this skill?");
    if (!confirmDelete) return;

    try {
      await SkillService.deleteSkill(skillId);
      alert("Skill deleted successfully.");

      setSkills((prevSkills) => {
        const updatedSkills = prevSkills.filter((skill) => skill.skillId !== skillId);
        const filtered = updatedSkills.filter((s) =>
          s.skillName?.toLowerCase().includes(search.toLowerCase())
        );
        const totalPages = Math.ceil(filtered.length / pageSize);
        const newPageIndex = pageIndex >= totalPages && totalPages > 0 ? totalPages - 1 : pageIndex;
        setManualPageIndex(newPageIndex);
        gotoPage(newPageIndex);
        return updatedSkills;
      });
    } catch (error) {
      alert(error.response?.data?.message || "Skill could not be deleted.");
    }
  };

  const filteredSkills = useMemo(() => {
    return skills.filter((s) =>
      s.skillName?.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, skills]);

  const columns = useMemo(
    () => [
      { Header: "Skill Id", accessor: "skillId" },
      { Header: "Skill Name", accessor: "skillName" },
      { Header: "Track", accessor: "track" },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="space-x-2">
            <button
              onClick={() => handleEdit(row.original)}
              className="bg-yellow-400 px-3 py-1 rounded hover:bg-yellow-500"
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(row.original.skillId)}
              className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            >
              Delete
            </button>
          </div>
        ),
        disableSortBy: true,
        disableFilters: true,
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredSkills,
      initialState: { pageIndex: manualPageIndex, pageSize: 5 },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    if (newlyAddedSkillId && filteredSkills.length > 0) {
      const index = filteredSkills.findIndex(skill => skill.skillId === newlyAddedSkillId);
      if (index !== -1) {
        const targetPage = Math.floor(index / pageSize);
        setManualPageIndex(targetPage);
        gotoPage(targetPage);
      }
      setNewlyAddedSkillId(null);
    }
  }, [filteredSkills, newlyAddedSkillId, pageSize, gotoPage]);

  const handleGotoPage = (pageNumber) => {
    setManualPageIndex(pageNumber);
    gotoPage(pageNumber);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4 text-center">Skill Management</h2>

      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row items-center gap-4 mb-6">
        <select
          name="track"
          value={selectedTrack}
          onChange={handleTrackChange}
          disabled={isEditMode}
          className="w-40 px-3 py-2 border rounded"
        >
          <option value="" disabled>Select Track</option>
          {tracks.map((track) => (
            <option key={track} value={track}>
              {track}
            </option>
          ))}
        </select>

        <input
          type="text"
          name="skillName"
          placeholder="Enter skill name"
          value={formData.skillName}
          onChange={handleInputChange}
          className="flex-1 px-3 py-2 border rounded"
        />

        <button
          type="submit"
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          {isEditMode ? "Update Skill" : "Add Skill"}
        </button>

        {isEditMode && (
          <button
            type="button"
            onClick={() => {
              setIsEditMode(false);
              setFormData({ skillId: null, skillName: "", track: selectedTrack });
              fetchSkillsByTrack(selectedTrack);
            }}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
        )}
      </form>

      <input
        type="text"
        placeholder="Search by skill name..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full mb-4 px-4 py-2 border border-gray-300 rounded"
      />

      <table {...getTableProps()} className="min-w-full bg-white border rounded shadow-sm">
        <thead className="bg-gray-100">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.id !== "Actions" ? column.getSortByToggleProps() : {})}
                  className="text-left px-4 py-2 border-b"
                >
                  {column.render("Header")}
                  {column.id !== "Actions" && (
                    <span className="ml-1 text-gray-400">
                      {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "⬍"}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} className="hover:bg-gray-50">
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} className="px-4 py-2 border-b">
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
        <div className="flex gap-2">
          <button
            onClick={() => handleGotoPage(pageIndex - 1)}
            disabled={!canPreviousPage}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => handleGotoPage(pageIndex + 1)}
            disabled={!canNextPage}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="text-sm">
          Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
        </div>
        <div>
          Go to page:{" "}
          <input
            type="number"
            min={1}
            max={pageOptions.length}
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
              handleGotoPage(pageNumber);
            }}
            className="w-16 px-2 py-1 border rounded"
          />
        </div>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="px-2 py-1 border rounded"
        >
          {[5, 10, 20].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default SkillManagementPage;
